import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { loadGoogleMaps, MOROCCO_CENTER } from "../lib/maps";
import { MapPin, Search, AlertCircle } from "lucide-react";

/**
 * Precise-location picker used on the florist signup form.
 * - Autocomplete search (Google Places, biased to Morocco)
 * - Draggable pin the shop owner drops on their front door
 * - Emits {lat, lng, place_id, formatted_address} to the parent form
 *
 * If the Google Maps key is missing or the JS API refuses to load, we fall
 * back to a plain lat/lng pair the owner can paste in — the form still ships.
 */
export default function LocationPicker({ value, onChange, cityHint }) {
  const mapEl = useRef(null);
  const searchEl = useRef(null);
  const markerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [manual, setManual] = useState({ lat: "", lng: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/bootstrap");
        const key = data?.maps?.google_maps_api_key || data?.google_maps_api_key;
        if (!key) { setStatus("nokey"); return; }
        const google = await loadGoogleMaps(key);
        if (cancelled) return;
        const start = value?.lat ? { lat: value.lat, lng: value.lng } :
                      cityHint || MOROCCO_CENTER;
        const map = new google.maps.Map(mapEl.current, {
          center: start, zoom: value?.lat ? 17 : 12,
          mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
          gestureHandling: "greedy",
        });
        const marker = new google.maps.Marker({
          position: start, map, draggable: true,
          animation: google.maps.Animation.DROP,
        });
        markerRef.current = marker;

        const emit = async (latLng) => {
          const geocoder = new google.maps.Geocoder();
          try {
            const res = await geocoder.geocode({ location: latLng });
            const first = res?.results?.[0];
            onChange({
              lat: latLng.lat(), lng: latLng.lng(),
              place_id: first?.place_id || "",
              formatted_address: first?.formatted_address || "",
            });
          } catch (_) {
            onChange({ lat: latLng.lat(), lng: latLng.lng(), place_id: "", formatted_address: "" });
          }
        };

        marker.addListener("dragend", () => emit(marker.getPosition()));
        map.addListener("click", (e) => {
          marker.setPosition(e.latLng); marker.setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(() => marker.setAnimation(null), 700);
          emit(e.latLng);
        });

        // Autocomplete biased to Morocco.
        const ac = new google.maps.places.Autocomplete(searchEl.current, {
          componentRestrictions: { country: "ma" },
          fields: ["place_id", "geometry", "formatted_address", "name"],
        });
        ac.addListener("place_changed", () => {
          const p = ac.getPlace();
          if (!p?.geometry?.location) return;
          const loc = p.geometry.location;
          map.panTo(loc); map.setZoom(17);
          marker.setPosition(loc); marker.setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(() => marker.setAnimation(null), 700);
          onChange({ lat: loc.lat(), lng: loc.lng(),
                     place_id: p.place_id || "",
                     formatted_address: p.formatted_address || p.name || "" });
        });

        setStatus("ready");
      } catch (_) {
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the pin synced if the parent resets the value.
  useEffect(() => {
    if (status !== "ready" || !markerRef.current) return;
    if (value?.lat && (markerRef.current.getPosition()?.lat() !== value.lat)) {
      markerRef.current.setPosition({ lat: value.lat, lng: value.lng });
    }
  }, [value, status]);

  if (status === "nokey" || status === "error") {
    // Graceful fallback — form still submits with manual coordinates.
    return (
      <div className="border border-dashed border-brass-light p-3 text-xs text-muted space-y-2" data-testid="apply-location-fallback">
        <p className="flex items-center gap-1.5"><AlertCircle size={13} /> La carte n'est pas disponible pour le moment. Collez les coordonnées GPS de votre boutique (clic droit sur Google Maps → "Copier les coordonnées").</p>
        <div className="grid grid-cols-2 gap-2">
          <input className="field" placeholder="Latitude ex. 33.5731" value={manual.lat}
                 onChange={(e) => setManual({ ...manual, lat: e.target.value })} />
          <input className="field" placeholder="Longitude ex. -7.5898" value={manual.lng}
                 onChange={(e) => setManual({ ...manual, lng: e.target.value })} />
        </div>
        <button type="button" className="btn-ghost text-xs"
                onClick={() => {
                  const lat = parseFloat(manual.lat), lng = parseFloat(manual.lng);
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                  onChange({ lat, lng, place_id: "", formatted_address: `${lat}, ${lng}` });
                }}>
          Utiliser ces coordonnées
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="apply-location-picker">
      <div className="relative">
        <Search size={13} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
        <input ref={searchEl} className="field ps-8" placeholder="Rechercher votre boutique sur Google Maps…"
               data-testid="apply-location-search" />
      </div>
      <div ref={mapEl} className="w-full h-64 border border-brass-light" data-testid="apply-location-map" />
      <p className="text-[11px] text-muted flex items-start gap-1">
        <MapPin size={11} className="mt-0.5 shrink-0 text-brass" />
        {value?.formatted_address
          ? <span data-testid="apply-location-selected"><b>Point de retrait :</b> {value.formatted_address}</span>
          : <span>Recherchez votre boutique ci-dessus ou cliquez sur la carte pour placer le point de retrait exact. Les livreurs partenaires verront cette adresse sur chaque commande.</span>}
      </p>
    </div>
  );
}
